import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import DesignPartnerDialog from "../components/argus/DesignPartnerDialog";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";

const plans = [
  {
    name: "Free",
    price: "$0",
    blurb: "Find your first critical today.",
    features: ["1 project", "5 scans / month", "SAST, DAST, SCA", "Evidence + Fix It panel", "Community support"],
    cta: "Start My Free Scan",
    to: "/signup",
    featured: false,
  },
  {
    name: "Pro",
    price: "$49",
    suffix: "/month",
    blurb: "For teams shipping every day.",
    features: ["25 projects", "Unlimited scans", "PDF reports", "API tokens + webhooks", "Priority support"],
    cta: "Upgrade to Pro",
    proCta: true,
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Talk to us",
    blurb: "SSO, on-prem, custom SLAs.",
    features: ["Unlimited projects", "SSO + SAML", "On-prem option", "DPA + Hall of Trust", "Dedicated security lead"],
    cta: "Talk to Security",
    to: null,
    talkToSecurity: true,
    featured: false,
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const proTarget = user ? "/app/settings?tab=billing" : "/signup";
  return (
    <div className="bg-paper text-ink min-h-screen">
      <MarketingHeader />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight" data-testid="pricing-title">
            Find It. Fix It. <span className="text-gold">Pay what's fair.</span>
          </h1>
          <p className="mt-4 text-lg text-ink/70">Three plans. No surprises. Cancel anytime.</p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {plans.map((p) => (
            <div
              key={p.name}
              data-testid={`plan-${p.name.toLowerCase()}`}
              className={`rounded-2xl p-8 border ${
                p.featured ? "bg-panel text-ink border-ink shadow-lg" : "bg-paper text-ink border-line shadow-sm"
              }`}
            >
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="font-display text-2xl">{p.name}</h3>
                {p.featured && <span className="text-xs font-semibold uppercase tracking-wider text-gold">Most popular</span>}
              </div>
              <div className="mb-2">
                <span className="text-4xl font-display tracking-tight">{p.price}</span>
                {p.suffix && <span className={`text-sm ${p.featured ? "text-ink/60" : "text-ink/60"}`}>{p.suffix}</span>}
              </div>
              <p className={`text-sm mb-6 ${p.featured ? "text-ink/60" : "text-ink/60"}`}>{p.blurb}</p>
              <ul className="space-y-2.5 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} className={p.featured ? "text-safe" : "text-safe"} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {p.talkToSecurity ? (
                <DesignPartnerDialog
                  source="pricing-enterprise"
                  trigger={
                    <Button
                      className="w-full h-12 font-semibold bg-gold text-paper hover:bg-gold2"
                      data-testid={`plan-cta-${p.name.toLowerCase()}`}
                    >
                      {p.cta}
                    </Button>
                  }
                />
              ) : (
                <Link to={p.proCta ? proTarget : p.to}>
                  <Button
                    className={`w-full h-12 font-semibold ${
                      p.featured ? "bg-gold hover:bg-gold2 text-paper" : "bg-gold text-paper hover:bg-gold2"
                    }`}
                    data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  >
                    {p.cta}
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-ink/50 mt-10">
          Every plan includes evidence-backed findings, OWASP/CWE mapping, and step-by-step fixes.
        </p>
      </section>
      <Footer />
    </div>
  );
}
